import { t } from 'i18next';
import { Candidate, QualificationType } from 'models/Candidate';
import { Vacancy } from 'models/Vacancy';
import { LanguageLevel } from 'searchality-data';
import utils from 'utils';
import DateService from './Date.service';

type QualKeyType = keyof Omit<
  QualificationType,
  'numberOfMatchingFields' | 'numberOfTotalFields'
>;

function getVacancyKey(
  qualKey: QualKeyType,
):
  | 'yearsOfExperienceRequired'
  | 'requiredLanguages'
  | 'isRightToWorkRequired'
  | Exclude<QualKeyType, 'yearsOfExperience' | 'languages' | 'rightToWork'> {
  if (qualKey === 'yearsOfExperience') {
    return 'yearsOfExperienceRequired';
  }

  if (qualKey === 'languages') {
    return 'requiredLanguages';
  }

  if (qualKey === 'rightToWork') {
    return 'isRightToWorkRequired';
  }

  return qualKey;
}

function getVacancyValue(qualKey: QualKeyType, vacancy: Vacancy) {
  const key = getVacancyKey(qualKey);

  if (key === 'requiredLanguages') {
    return vacancy[key]?.length;
  }

  if (key === 'startDate') {
    if (vacancy?.[key]) {
      return DateService.formatDate(vacancy[key]);
    }
    return t('immediately');
  }

  return vacancy[key];
}

function getCandidateKey(
  qualkey: QualKeyType,
):
  | 'languageRequirement'
  | 'earliestAvailableStartingDate'
  | 'education'
  | 'doesHaveExperienceInTheField'
  | Exclude<
      QualKeyType,
      'languages' | 'startDate' | 'educationLevel' | 'curriculumExperience'
    > {
  if (qualkey === 'languages') {
    return 'languageRequirement';
  }
  if (qualkey === 'startDate') {
    return 'earliestAvailableStartingDate';
  }

  if (qualkey === 'educationLevel') {
    return 'education';
  }

  if (qualkey === 'curriculumExperience') {
    return 'doesHaveExperienceInTheField';
  }

  return qualkey;
}

function getCandidateValue(qualkey: QualKeyType, candidate: Candidate) {
  const key = getCandidateKey(qualkey);

  if (key === 'languageRequirement') {
    return candidate['languageRequirement']?.length;
  }

  if (key === 'earliestAvailableStartingDate') {
    if (candidate[key]) {
      return DateService.formatDate(candidate[key]);
    } else {
      return t('requestedDate');
    }
  }

  if (key === 'education')
    return candidate[key]?.reduce(
      (prev, curr, index) =>
        index > 0 ? prev + ', ' + curr.degreeLevel : curr.degreeLevel,
      '',
    );

  if (key === 'doesHaveExperienceInTheField') {
    if (candidate[key]) {
      return t(
        'QualificationDescription.candidateHave.HaveCurriculumExperience',
      );
    }
    return t(
      'QualificationDescription.candidateHave.DontHaveCurriculumExperience',
    );
  }

  if (key === 'yearsOfExperience') {
    if (candidate[key]) {
      return candidate[key];
    }
    return t('noneYears');
  }

  if (key === 'rightToWork') {
    if (candidate['hasRightToWork']) {
      return t('QualificationDescription.candidateHave.HaveRightToWork', {
        rightToWork: candidate['rightToWork']?.[0],
      });
    }
    return t('QualificationDescription.candidateHave.DontHaveRightToWork');
  }

  return candidate[key];
}

function getCandidatesQualificationVariantAndMessage(
  qualifications: number,
  numberOfTotalFields: number,
): ['green' | 'red' | 'blue', string] {
  if (qualifications < numberOfTotalFields / 2) {
    return ['red', t('candidateDoesNotFitRequirements')];
  }

  if (qualifications === numberOfTotalFields) {
    return ['green', t('candidateFitAllRequirements')];
  }

  return ['blue', t('candidateFitMostOfRequirements')];
}

function getCandidateLanguagesMatch(candidate: Candidate) {
  const { vacancy, languageRequirement } = candidate;
  const { requiredLanguages } = vacancy;

  const speakedLanguages = languageRequirement.filter(
    ({ doesSpeak }) => doesSpeak,
  );

  return speakedLanguages.filter(({ language, level }) => {
    const tmp = requiredLanguages.find((el) => el.language === language);

    return !utils.isLanguageLevelOkay(tmp.level as LanguageLevel, level);
  });
}

function getCurriculumExperienceAdditionalDescription(candidate: Candidate) {
  const {
    qualifications,
    yearsOfExperienceInTheField,
    doesHaveExperienceInTheField,
  } = candidate;

  if (qualifications.curriculumExperience) return '';

  if (doesHaveExperienceInTheField && yearsOfExperienceInTheField) {
    return `${t(
      'QualificationDescription.curriculumExperienceExplanation1',
    )} ${yearsOfExperienceInTheField} ${t(
      'QualificationDescription.curriculumExperienceExplanation2',
    )}`;
  }

  return '';
}

export default {
  getVacancyKey,
  getVacancyValue,
  getCandidateKey,
  getCandidateValue,
  getCandidatesQualificationVariantAndMessage,
  getCurriculumExperienceAdditionalDescription,
  getCandidateLanguagesMatch,
};
